import {useState, useCallback} from "react";
import {
  voicePlayerManager,
  calculatePlaybackDuration,
  generateVoiceSequence,
} from "./voicePlayer";

type PlaybackState = "idle" | "playing" | "error";

type UseVoicePlayerReturn = {
  playbackState: PlaybackState;
  playVoiceForText: (text: string, intent: string) => Promise<void>;
  stopVoice: () => void;
  activateVoice: () => Promise<void>;
  deactivateVoice: () => void;
  isVoiceActivated: boolean;
};

/**
 * 텍스트 길이에 따라 자동으로 음성 파일 시퀀스를 생성하고 재생합니다.
 */
export const useVoicePlayer = (
  artist: string,
  character: string,
): UseVoicePlayerReturn => {
  const [playbackState, setPlaybackState] = useState<PlaybackState>("idle");
  const [isVoiceActivated, setIsVoiceActivated] = useState<boolean>(false);

  const playVoiceForText = useCallback(
    async (text: string, intent: string) => {
      try {
        setPlaybackState("playing");
        const targetDuration = calculatePlaybackDuration(text.length);
        const voiceSequence = generateVoiceSequence(
          targetDuration,
          artist,
          character,
          intent,
        );

        if (voiceSequence.length === 0) {
          setPlaybackState("idle");
          return;
        }

        await voicePlayerManager.playVoiceSequence(
          voiceSequence,
          artist,
          character,
        );
        setPlaybackState("idle");
      } catch (error) {
        console.error("Error playing voice:", error);
        setPlaybackState("error");
      }
    },
    [artist, character],
  );

  const stopVoice = useCallback(() => {
    voicePlayerManager.stopAll();
    setPlaybackState("idle");
  }, []);

  /**
   * 브라우저 자동재생 정책을 우회하기 위해 오디오를 활성화합니다.
   * 유저 액션을 통해 호출되어야 합니다.
   */
  const activateVoice = useCallback(async () => {
    await voicePlayerManager.activateAudio();
    setIsVoiceActivated(true);
  }, []);

  /**
   * 오디오 활성화 상태를 해제합니다.
   * 재생 중이면 모든 재생을 중지합니다.
   */
  const deactivateVoice = useCallback(() => {
    voicePlayerManager.deactivateAudio();
    setIsVoiceActivated(false);
    stopVoice();
  }, [stopVoice]);

  return {
    playbackState,
    playVoiceForText,
    stopVoice,
    activateVoice,
    deactivateVoice,
    isVoiceActivated,
  };
};
